import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { apiWorker } from '../../api/worker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import { Trophy, RotateCcw, Volume2, VolumeX, Maximize2, Minimize2 } from 'lucide-react';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';

// ---------------------------------------------------------------------------
// Sound FX (Web Audio API Synthesizer)
// ---------------------------------------------------------------------------

const playRaceSound = (type: 'countdown' | 'go' | 'lap' | 'crash' | 'boost' | 'finish' | 'drift' | 'oil') => {
  if (typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;

    if (type === 'countdown') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(440, now);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now); osc.stop(now + 0.15);
    } else if (type === 'go') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(880, now);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now); osc.stop(now + 0.25);
    } else if (type === 'lap') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.07);
      osc.frequency.setValueAtTime(784, now + 0.14);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now); osc.stop(now + 0.25);
    } else if (type === 'crash') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(50, now + 0.2);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc.start(now); osc.stop(now + 0.2);
    } else if (type === 'boost') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.2);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now); osc.stop(now + 0.25);
    } else if (type === 'finish') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.1);
      osc.frequency.setValueAtTime(784, now + 0.2);
      osc.frequency.setValueAtTime(1047, now + 0.3);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now); osc.stop(now + 0.5);
    } else if (type === 'drift') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(80, now);
      osc.frequency.setValueAtTime(100, now + 0.05);
      gain.gain.setValueAtTime(0.04, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now); osc.stop(now + 0.08);
    } else if (type === 'oil') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.setValueAtTime(80, now + 0.15);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc.start(now); osc.stop(now + 0.2);
    }
  } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function useIsPortraitMobile(): boolean {
  const [isPortrait, setIsPortrait] = useState(false);
  useEffect(() => {
    const check = () => {
      setIsPortrait(isMobileDevice() && window.innerHeight > window.innerWidth);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return isPortrait;
}

async function tryLockLandscape(): Promise<boolean> {
  try {
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    if (so?.lock) { await so.lock('landscape'); return true; }
  } catch { /* ignore */ }
  return false;
}

function tryUnlockOrientation() {
  try {
    const so = screen.orientation as ScreenOrientation & { unlock?: () => void };
    if (so?.unlock) so.unlock();
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Vec2 { x: number; y: number; }

interface BoostPad {
  x: number; y: number; angle: number;
}

interface OilSlick {
  x: number; y: number; radius: number;
}

interface SceneryItem {
  x: number; y: number;
  type: 'mug' | 'stapler' | 'paperclip' | 'pencil' | 'calculator' | 'plant' | 'ruler' | 'postit';
  size: number;
  rotation?: number;
}

interface CarState {
  x: number; y: number;
  vx: number; vy: number;
  angle: number;
  isPlayer: boolean;
  name: string;
  color: string;
  lap: number;
  lapStartFrame: number;
  lapTimes: number[];
  checkpointsPassed: Set<number>;
  prevFinishDist: number;
  finished: boolean;
  finishFrame: number;
  position: number;
  lastPosition: number;
  posChangeTimer: number;
  boostTimer: number;
  oilTimer: number;
  // AI fields
  targetWP: number;
  skill: number;
  maxSpd: number;
  wobble: number;
}

interface TrackDef {
  id: string;
  name: string;
  tagline: string;
  emoji: string;
  centerLine: Vec2[];
  width: number;
  scenery: SceneryItem[];
  boostPads: BoostPad[];
  oilSlicks: OilSlick[];
  bgColor: string;
}

interface SkidMark {
  x: number; y: number;
  alpha: number;
}

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

interface LeaderboardEntry {
  playerName: string;
  score?: number;
  track?: string;
  bestLap?: number;
  totalTime: number;
  date?: string;
  metadata?: Record<string, any>;
}

interface ResultEntry {
  name: string;
  color: string;
  position: number;
  totalFrames: number;
  bestLap: number;
  finished: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CANVAS_W = 800;
const CANVAS_H = 500;
const CAR_L = 32;
const CAR_W = 16;
const ACCEL = 0.14;
const BRAKE_FORCE = 0.12;
const DRAG = 0.986;
const OFF_TRACK_DRAG = 0.955;
const TURN_SPEED = 0.045;
const LATERAL_FRICTION = 0.88;
const OIL_LATERAL_FRICTION = 0.55;
const MAX_SPEED = 5.5;
const BOOST_SPEED_MULT = 1.45;
const BOOST_DURATION = 90;
const OIL_DURATION = 60;
const TOTAL_LAPS = 3;
const COUNTDOWN_FRAMES = 240;

// ---------------------------------------------------------------------------
// Track Definitions
// ---------------------------------------------------------------------------

function midpointOfSegment(pts: Vec2[], i: number): { x: number; y: number; angle: number } {
  const a = pts[i % pts.length];
  const b = pts[(i + 1) % pts.length];
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
    angle: Math.atan2(b.y - a.y, b.x - a.x),
  };
}

const BOARDROOM_PTS: Vec2[] = [
  { x: 1000, y: 320 }, { x: 1180, y: 330 }, { x: 1340, y: 380 },
  { x: 1460, y: 470 }, { x: 1530, y: 590 }, { x: 1550, y: 730 },
  { x: 1510, y: 860 }, { x: 1420, y: 960 }, { x: 1280, y: 1040 },
  { x: 1100, y: 1070 }, { x: 920, y: 1050 }, { x: 760, y: 980 },
  { x: 640, y: 870 }, { x: 560, y: 730 }, { x: 530, y: 590 },
  { x: 560, y: 460 }, { x: 650, y: 380 }, { x: 810, y: 330 },
];
const MAILROOM_PTS: Vec2[] = [
  { x: 800, y: 300 }, { x: 1050, y: 280 }, { x: 1250, y: 320 },
  { x: 1400, y: 420 }, { x: 1450, y: 560 }, { x: 1380, y: 680 },
  { x: 1250, y: 730 }, { x: 1150, y: 800 }, { x: 1250, y: 870 },
  { x: 1350, y: 950 }, { x: 1300, y: 1080 }, { x: 1100, y: 1130 },
  { x: 850, y: 1100 }, { x: 650, y: 1000 }, { x: 550, y: 860 },
  { x: 500, y: 700 }, { x: 520, y: 540 }, { x: 600, y: 400 },
];
const EXEC_PTS: Vec2[] = [
  { x: 700, y: 350 }, { x: 950, y: 300 }, { x: 1200, y: 320 },
  { x: 1380, y: 400 }, { x: 1480, y: 540 }, { x: 1500, y: 700 },
  { x: 1450, y: 840 }, { x: 1300, y: 920 }, { x: 1150, y: 880 },
  { x: 1050, y: 960 }, { x: 900, y: 1050 }, { x: 700, y: 1080 },
  { x: 520, y: 1000 }, { x: 420, y: 860 }, { x: 380, y: 700 },
  { x: 400, y: 540 }, { x: 480, y: 420 },
];

const TRACKS: TrackDef[] = [
  {
    id: 'boardroom',
    name: 'The Boardroom',
    tagline: 'Executive oval — learn the ropes',
    emoji: '🏢',
    width: 110,
    bgColor: '#4a3426',
    centerLine: BOARDROOM_PTS,
    boostPads: [
      midpointOfSegment(BOARDROOM_PTS, 3),
      midpointOfSegment(BOARDROOM_PTS, 9),
      midpointOfSegment(BOARDROOM_PTS, 15),
    ],
    oilSlicks: [
      { ...midpointOfSegment(BOARDROOM_PTS, 6), radius: 22 },
      { ...midpointOfSegment(BOARDROOM_PTS, 12), radius: 20 },
    ],
    scenery: [
      { x: 1020, y: 700, type: 'mug', size: 50 },
      { x: 780, y: 580, type: 'paperclip', size: 35 },
      { x: 1250, y: 750, type: 'pencil', size: 40, rotation: 0.4 },
      { x: 900, y: 500, type: 'postit', size: 35 },
      { x: 1100, y: 880, type: 'calculator', size: 40 },
      { x: 700, y: 700, type: 'plant', size: 45 },
    ],
  },
  {
    id: 'mailroom',
    name: 'The Mailroom',
    tagline: 'Tight turns and long straights',
    emoji: '📦',
    width: 100,
    bgColor: '#3d3028',
    centerLine: MAILROOM_PTS,
    boostPads: [
      midpointOfSegment(MAILROOM_PTS, 2),
      midpointOfSegment(MAILROOM_PTS, 8),
      midpointOfSegment(MAILROOM_PTS, 14),
    ],
    oilSlicks: [
      { ...midpointOfSegment(MAILROOM_PTS, 5), radius: 20 },
      { ...midpointOfSegment(MAILROOM_PTS, 11), radius: 22 },
    ],
    scenery: [
      { x: 950, y: 650, type: 'stapler', size: 45 },
      { x: 1150, y: 500, type: 'ruler', size: 50, rotation: -0.3 },
      { x: 750, y: 800, type: 'mug', size: 42 },
      { x: 1100, y: 980, type: 'pencil', size: 38, rotation: 0.8 },
      { x: 700, y: 600, type: 'plant', size: 40 },
    ],
  },
  {
    id: 'executive',
    name: 'Executive Suite',
    tagline: 'Technical circuit — only the brave',
    emoji: '👔',
    width: 90,
    bgColor: '#2d2a36',
    centerLine: EXEC_PTS,
    boostPads: [
      midpointOfSegment(EXEC_PTS, 1),
      midpointOfSegment(EXEC_PTS, 7),
      midpointOfSegment(EXEC_PTS, 12),
    ],
    oilSlicks: [
      { ...midpointOfSegment(EXEC_PTS, 4), radius: 18 },
      { ...midpointOfSegment(EXEC_PTS, 10), radius: 20 },
    ],
    scenery: [
      { x: 950, y: 650, type: 'calculator', size: 48 },
      { x: 1200, y: 700, type: 'stapler', size: 40 },
      { x: 750, y: 750, type: 'postit', size: 35 },
      { x: 1050, y: 500, type: 'ruler', size: 55, rotation: -0.6 },
      { x: 600, y: 850, type: 'mug', size: 46 },
    ],
  },
];

// ---------------------------------------------------------------------------
// AI Drivers
// ---------------------------------------------------------------------------

const AI_DRIVERS = [
  { name: 'The Intern', color: '#FF4444', skill: 0.62, emoji: '🧑‍💼' },
  { name: 'VP of Ops', color: '#44DD44', skill: 0.76, emoji: '👔' },
  { name: 'The CEO',   color: '#DD44FF', skill: 0.88, emoji: '👑' },
];

// ---------------------------------------------------------------------------
// Geometry Helpers
// ---------------------------------------------------------------------------

function vecNormalize(v: Vec2): Vec2 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y);
  return len > 0 ? { x: v.x / len, y: v.y / len } : { x: 0, y: 0 };
}

function pointToSegmentDistSq(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const abx = bx - ax, aby = by - ay;
  const apx = px - ax, apy = py - ay;
  const dot = apx * abx + apy * aby;
  const lenSq = abx * abx + aby * aby;
  const t = lenSq > 0 ? Math.max(0, Math.min(1, dot / lenSq)) : 0;
  const cx = ax + t * abx, cy = ay + t * aby;
  const dx = px - cx, dy = py - cy;
  return dx * dx + dy * dy;
}

function isOnTrack(x: number, y: number, track: TrackDef): boolean {
  const pts = track.centerLine;
  const halfW = track.width / 2;
  const threshSq = halfW * halfW;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    if (pointToSegmentDistSq(x, y, pts[i].x, pts[i].y, pts[j].x, pts[j].y) < threshSq) {
      return true;
    }
  }
  return false;
}

function nearestWaypoint(x: number, y: number, track: TrackDef): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < track.centerLine.length; i++) {
    const dx = x - track.centerLine[i].x;
    const dy = y - track.centerLine[i].y;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

function getRaceProgress(car: CarState, track: TrackDef): number {
  const pts = track.centerLine;
  const n = pts.length;
  let bestDistSq = Infinity;
  let bestSeg = 0;
  let bestT = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = pts[i].x, ay = pts[i].y;
    const bx = pts[j].x, by = pts[j].y;
    const abx = bx - ax, aby = by - ay;
    const apx = car.x - ax, apy = car.y - ay;
    const lenSq = abx * abx + aby * aby;
    const t = lenSq > 0 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / lenSq)) : 0;
    const cx = ax + t * abx, cy = ay + t * aby;
    const dx = car.x - cx, dy = car.y - cy;
    const dSq = dx * dx + dy * dy;
    if (dSq < bestDistSq) { bestDistSq = dSq; bestSeg = i; bestT = t; }
  }
  return car.lap * n + bestSeg + bestT;
}

function getStartDir(track: TrackDef): Vec2 {
  const last = track.centerLine[track.centerLine.length - 1];
  const second = track.centerLine[1];
  return vecNormalize({ x: (second.x - last.x), y: (second.y - last.y) });
}

function getFinishDist(x: number, y: number, track: TrackDef, fwd: Vec2): number {
  const p0 = track.centerLine[0];
  return (x - p0.x) * fwd.x + (y - p0.y) * fwd.y;
}

function resolveCarCollision(a: CarState, b: CarState) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distSq = dx * dx + dy * dy;
  const minDist = CAR_L * 0.85;
  if (distSq < minDist * minDist && distSq > 0) {
    const dist = Math.sqrt(distSq);
    const nx = dx / dist, ny = dy / dist;
    const overlap = minDist - dist;
    a.x -= nx * overlap * 0.5;
    a.y -= ny * overlap * 0.5;
    b.x += nx * overlap * 0.5;
    b.y += ny * overlap * 0.5;
    const relVn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
    if (relVn < 0) {
      a.vx += nx * relVn * 0.35;
      a.vy += ny * relVn * 0.35;
      b.vx -= nx * relVn * 0.35;
      b.vy -= ny * relVn * 0.35;
    }
  }
}

// ---------------------------------------------------------------------------
// Drawing Helpers
// ---------------------------------------------------------------------------

/** Seeded pseudo-random for deterministic noise textures */
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function drawTrack(ctx: CanvasRenderingContext2D, track: TrackDef, frameCount: number) {
  const pts = track.centerLine;

  // Track border / curbs — alternating red-white blocks
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
  ctx.lineWidth = track.width + 14;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Outer curb: alternating red/white
  ctx.setLineDash([16, 16]);
  ctx.strokeStyle = '#cc2200';
  ctx.stroke();
  ctx.lineDashOffset = 16;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;

  // Track surface (dark asphalt)
  ctx.lineWidth = track.width;
  ctx.strokeStyle = '#3a3a3a';
  ctx.stroke();

  // Asphalt texture noise (deterministic dots)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
  ctx.lineWidth = track.width - 4;
  ctx.strokeStyle = 'transparent';
  // We'll draw dots along the track path
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const segLen = Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
    const steps = Math.floor(segLen / 6);
    const nx = -(b.y - a.y) / segLen;
    const ny = (b.x - a.x) / segLen;
    for (let j = 0; j < steps; j++) {
      const t = j / steps;
      const mx = a.x + (b.x - a.x) * t;
      const my = a.y + (b.y - a.y) * t;
      const seed = i * 1000 + j;
      const hw = track.width / 2 - 6;
      // 3-4 random dots per step across the width
      for (let k = 0; k < 3; k++) {
        const r1 = seededRandom(seed + k * 7);
        const r2 = seededRandom(seed + k * 13 + 3);
        const r3 = seededRandom(seed + k * 19 + 5);
        const offX = (r1 - 0.5) * 2 * hw;
        const offY = (r2 - 0.5) * 2 * hw;
        const px = mx + nx * offX;
        const py = my + ny * offY;
        const brightness = 45 + r3 * 25;
        ctx.fillStyle = `rgb(${brightness},${brightness},${brightness})`;
        ctx.fillRect(px, py, 1.5, 1.5);
      }
    }
  }
  ctx.restore();

  // Racing line (faint tire marks along center)
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(20,20,20,0.15)';
  ctx.stroke();

  // Center dashed line
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
  ctx.lineWidth = 2;
  ctx.setLineDash([14, 16]);
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.stroke();
  ctx.setLineDash([]);

  // Start/finish checkerboard
  const fwd = getStartDir(track);
  const p0 = pts[0];
  ctx.save();
  ctx.translate(p0.x, p0.y);
  ctx.rotate(Math.atan2(fwd.y, fwd.x));
  const checkSize = 8;
  const numChecks = Math.floor(track.width / checkSize);
  for (let i = 0; i < numChecks; i++) {
    for (let j = 0; j < 3; j++) {
      ctx.fillStyle = (i + j) % 2 === 0 ? '#fff' : '#111';
      ctx.fillRect(
        (j - 1) * checkSize,
        (i - numChecks / 2) * checkSize,
        checkSize, checkSize
      );
    }
  }
  ctx.restore();

  // Draw boost pads
  for (const bp of track.boostPads) {
    drawBoostPad(ctx, bp, frameCount);
  }

  // Draw oil slicks
  for (const os of track.oilSlicks) {
    drawOilSlick(ctx, os);
  }
}

function drawBoostPad(ctx: CanvasRenderingContext2D, bp: BoostPad, frameCount: number) {
  ctx.save();
  ctx.translate(bp.x, bp.y);
  ctx.rotate(bp.angle);

  // Base plate
  ctx.fillStyle = 'rgba(0,80,255,0.35)';
  ctx.fillRect(-20, -14, 40, 28);
  ctx.strokeStyle = 'rgba(0,120,255,0.6)';
  ctx.lineWidth = 2;
  ctx.strokeRect(-20, -14, 40, 28);

  // Chevron arrows (animated pulse)
  const pulse = 0.6 + Math.sin(frameCount * 0.15) * 0.3;
  ctx.fillStyle = `rgba(100,180,255,${pulse})`;
  for (let i = 0; i < 3; i++) {
    const ox = -12 + i * 10;
    ctx.beginPath();
    ctx.moveTo(ox, -8);
    ctx.lineTo(ox + 6, 0);
    ctx.lineTo(ox, 8);
    ctx.lineTo(ox + 3, 8);
    ctx.lineTo(ox + 9, 0);
    ctx.lineTo(ox + 3, -8);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

function drawOilSlick(ctx: CanvasRenderingContext2D, os: OilSlick) {
  ctx.save();
  ctx.translate(os.x, os.y);

  // Dark base
  ctx.beginPath();
  ctx.ellipse(0, 0, os.radius, os.radius * 0.7, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(10,10,10,0.6)';
  ctx.fill();

  // Rainbow sheen
  const iridGrad = ctx.createRadialGradient(-3, -2, 0, 0, 0, os.radius * 0.8);
  iridGrad.addColorStop(0, 'rgba(120,0,200,0.2)');
  iridGrad.addColorStop(0.3, 'rgba(0,100,200,0.15)');
  iridGrad.addColorStop(0.6, 'rgba(0,180,80,0.12)');
  iridGrad.addColorStop(1, 'rgba(200,180,0,0.08)');
  ctx.fillStyle = iridGrad;
  ctx.fill();

  // Edge highlight
  ctx.strokeStyle = 'rgba(100,100,100,0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();
}

function drawCar(ctx: CanvasRenderingContext2D, car: CarState, frameCount: number) {
  ctx.save();
  ctx.translate(car.x, car.y);
  ctx.rotate(car.angle);

  const hl = CAR_L / 2;
  const hw = CAR_W / 2;

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.3)';
  ctx.beginPath();
  ctx.ellipse(2, 2, hl + 1, hw + 1, 0, 0, Math.PI * 2);
  ctx.fill();

  // Body (rounded rectangle)
  ctx.beginPath();
  ctx.moveTo(-hl + 4, -hw);
  ctx.lineTo(hl - 4, -hw);
  ctx.quadraticCurveTo(hl, -hw, hl, -hw + 4);
  ctx.lineTo(hl, hw - 4);
  ctx.quadraticCurveTo(hl, hw, hl - 4, hw);
  ctx.lineTo(-hl + 4, hw);
  ctx.quadraticCurveTo(-hl, hw, -hl, hw - 3);
  ctx.lineTo(-hl, -hw + 3);
  ctx.quadraticCurveTo(-hl, -hw, -hl + 4, -hw);
  ctx.closePath();
  ctx.fillStyle = car.color;
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Darker roof/cabin inset
  ctx.fillStyle = darkenColor(car.color, 30);
  ctx.fillRect(-3, -hw + 3, 10, CAR_W - 6);

  // Windshield (front glass)
  ctx.fillStyle = 'rgba(130,200,255,0.65)';
  ctx.beginPath();
  ctx.moveTo(hl - 10, -hw + 3);
  ctx.lineTo(hl - 4, -hw + 2);
  ctx.lineTo(hl - 4, hw - 2);
  ctx.lineTo(hl - 10, hw - 3);
  ctx.closePath();
  ctx.fill();

  // Windshield reflection
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(hl - 8, -hw + 4);
  ctx.lineTo(hl - 6, hw - 4);
  ctx.stroke();

  // Racing stripe
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.fillRect(-hl, -1.5, CAR_L, 3);

  // Headlights (2 bright dots at front)
  ctx.fillStyle = '#ffffcc';
  ctx.shadowColor = 'rgba(255,255,200,0.6)';
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.arc(hl - 1, -hw + 4, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(hl - 1, hw - 4, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Taillights (2 red rectangles at rear)
  ctx.fillStyle = '#ff2222';
  ctx.shadowColor = 'rgba(255,0,0,0.4)';
  ctx.shadowBlur = 3;
  ctx.fillRect(-hl, -hw + 2, 3, 4);
  ctx.fillRect(-hl, hw - 6, 3, 4);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Wheels (dark)
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(-hl + 3, -hw - 2, 7, 3);
  ctx.fillRect(-hl + 3, hw - 1, 7, 3);
  ctx.fillRect(hl - 10, -hw - 2, 7, 3);
  ctx.fillRect(hl - 10, hw - 1, 7, 3);
  // Wheel highlight
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.fillRect(-hl + 4, -hw - 1, 5, 1);
  ctx.fillRect(-hl + 4, hw, 5, 1);
  ctx.fillRect(hl - 9, -hw - 1, 5, 1);
  ctx.fillRect(hl - 9, hw, 5, 1);

  // Player spoiler
  if (car.isPlayer) {
    ctx.fillStyle = darkenColor(car.color, 40);
    ctx.fillRect(-hl - 3, -hw + 2, 4, CAR_W - 4);
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(-hl - 3, -hw + 2, 4, CAR_W - 4);
  }

  // Boost flame effect
  if (car.boostTimer > 0) {
    const flameLen = 10 + Math.random() * 8;
    const innerLen = flameLen * 0.6;
    // Outer flame (blue)
    ctx.fillStyle = `rgba(0,100,255,${0.4 + Math.random() * 0.3})`;
    ctx.beginPath();
    ctx.moveTo(-hl, -4);
    ctx.lineTo(-hl - flameLen, 0);
    ctx.lineTo(-hl, 4);
    ctx.closePath();
    ctx.fill();
    // Inner flame (white)
    ctx.fillStyle = `rgba(200,220,255,${0.3 + Math.random() * 0.3})`;
    ctx.beginPath();
    ctx.moveTo(-hl, -2);
    ctx.lineTo(-hl - innerLen, 0);
    ctx.lineTo(-hl, 2);
    ctx.closePath();
    ctx.fill();
  }

  // Racing number (player car)
  if (car.isPlayer) {
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = 'bold 7px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('1', 4, 0);
  }

  ctx.restore();
}

function drawSceneryItem(ctx: CanvasRenderingContext2D, item: SceneryItem) {
  ctx.save();
  ctx.translate(item.x, item.y);
  if (item.rotation) ctx.rotate(item.rotation);
  const s = item.size;

  if (item.type === 'mug') {
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.15)';
    ctx.beginPath();
    ctx.ellipse(3, s * 0.35, s * 0.35, s * 0.15, 0, 0, Math.PI * 2);
    ctx.fill();
    // Body (cylinder viewed from above-ish)
    ctx.fillStyle = '#e8e0d0';
    ctx.beginPath();
    ctx.ellipse(0, 0, s * 0.3, s * 0.35, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#b5a898';
    ctx.lineWidth = 2;
    ctx.stroke();
    // Coffee inside (dark circle)
    ctx.fillStyle = '#3d2817';
    ctx.beginPath();
    ctx.ellipse(0, -1, s * 0.22, s * 0.26, 0, 0, Math.PI * 2);
    ctx.fill();
    // Handle
    ctx.strokeStyle = '#c5b8a8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(s * 0.32, 0, s * 0.12, -1.2, 1.2);
    ctx.stroke();
    // Steam wisps
    ctx.strokeStyle = 'rgba(200,200,200,0.25)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 2; i++) {
      const sx = -4 + i * 8;
      ctx.beginPath();
      ctx.moveTo(sx, -s * 0.32);
      ctx.quadraticCurveTo(sx + 3, -s * 0.45, sx - 2, -s * 0.55);
      ctx.stroke();
    }
  } else if (item.type === 'stapler') {
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(-s * 0.4 + 2, -s * 0.15 + 2, s * 0.8, s * 0.3);
    // Base
    ctx.fillStyle = '#222';
    ctx.fillRect(-s * 0.4, -s * 0.12, s * 0.8, s * 0.24);
    // Top arm
    ctx.fillStyle = '#cc2233';
    ctx.fillRect(-s * 0.38, -s * 0.18, s * 0.7, s * 0.12);
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(-s * 0.36, -s * 0.17, s * 0.66, s * 0.04);
    // Front opening
    ctx.fillStyle = '#999';
    ctx.fillRect(s * 0.28, -s * 0.1, s * 0.1, s * 0.2);
  } else if (item.type === 'paperclip') {
    ctx.strokeStyle = '#c0c0c0';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    const w = s * 0.2, h = s * 0.4;
    ctx.beginPath();
    ctx.moveTo(-w, h);
    ctx.lineTo(-w, -h + w);
    ctx.arc(0, -h + w, w, Math.PI, 0);
    ctx.lineTo(w, h - w * 0.6);
    ctx.arc(w * 0.3, h - w * 0.6, w * 0.7, 0, Math.PI, true);
    ctx.lineTo(-w * 0.4, -h + w * 1.5);
    ctx.stroke();
    // Shine
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-w + 1, h - 5);
    ctx.lineTo(-w + 1, -h + w + 3);
    ctx.stroke();
  } else if (item.type === 'pencil') {
    const len = s * 0.8;
    const th = s * 0.12;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    ctx.fillRect(-len / 2 + 2, -th / 2 + 2, len, th);
    // Body (yellow)
    ctx.fillStyle = '#f5c518';
    ctx.fillRect(-len / 2, -th / 2, len * 0.75, th);
    // Wood + graphite tip
    ctx.fillStyle = '#d4a574';
    ctx.fillRect(-len / 2 + len * 0.75, -th / 2, len * 0.15, th);
    // Tip point
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.moveTo(-len / 2 + len * 0.9, -th / 2);
    ctx.lineTo(-len / 2 + len, 0);
    ctx.lineTo(-len / 2 + len * 0.9, th / 2);
    ctx.closePath();
    ctx.fill();
    // Eraser (pink)
    ctx.fillStyle = '#e8849a';
    ctx.fillRect(-len / 2, -th / 2, len * 0.08, th);
    // Metal band
    ctx.fillStyle = '#aaa';
    ctx.fillRect(-len / 2 + len * 0.08, -th / 2, len * 0.04, th);
    // Stripe
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.fillRect(-len / 2, -1, len * 0.75, 2);
  } else if (item.type === 'calculator') {
    const w = s * 0.55, h = s * 0.7;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(-w / 2 + 2, -h / 2 + 2, w, h);
    // Body
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(-w / 2, -h / 2, w, h);
    // Screen
    ctx.fillStyle = '#88aa88';
    ctx.fillRect(-w / 2 + 3, -h / 2 + 3, w - 6, h * 0.22);
    // Screen text
    ctx.fillStyle = '#223322';
    ctx.font = `bold ${h * 0.12}px monospace`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText('1337', w / 2 - 5, -h / 2 + 3 + h * 0.11);
    // Buttons grid
    ctx.fillStyle = '#555';
    const bRows = 4, bCols = 4;
    const bw = (w - 10) / bCols, bh = (h * 0.55) / bRows;
    const startY = -h / 2 + h * 0.32;
    for (let r = 0; r < bRows; r++) {
      for (let c = 0; c < bCols; c++) {
        const bx = -w / 2 + 4 + c * bw;
        const by = startY + r * bh;
        ctx.fillStyle = (c === 3) ? '#cc6633' : '#555';
        ctx.fillRect(bx, by, bw - 2, bh - 2);
      }
    }
  } else if (item.type === 'plant') {
    // Pot
    ctx.fillStyle = '#a06040';
    ctx.fillRect(-s * 0.15, s * 0.1, s * 0.3, s * 0.2);
    ctx.fillRect(-s * 0.18, s * 0.08, s * 0.36, s * 0.06);
    // Soil
    ctx.fillStyle = '#4a3020';
    ctx.beginPath();
    ctx.ellipse(0, s * 0.1, s * 0.14, s * 0.05, 0, 0, Math.PI * 2);
    ctx.fill();
    // Leaves (multiple green circles)
    ctx.fillStyle = '#2d8a4e';
    ctx.beginPath();
    ctx.arc(0, -s * 0.1, s * 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3ca55c';
    ctx.beginPath();
    ctx.arc(-s * 0.1, -s * 0.05, s * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(s * 0.1, 0, s * 0.12, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#4cc06c';
    ctx.beginPath();
    ctx.arc(s * 0.05, -s * 0.18, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
  } else if (item.type === 'ruler') {
    const len = s * 0.9, th = s * 0.14;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    ctx.fillRect(-len / 2 + 2, -th / 2 + 2, len, th);
    // Body
    ctx.fillStyle = '#f0e8c8';
    ctx.fillRect(-len / 2, -th / 2, len, th);
    ctx.strokeStyle = '#b8a878';
    ctx.lineWidth = 1;
    ctx.strokeRect(-len / 2, -th / 2, len, th);
    // Markings
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 12; i++) {
      const mx = -len / 2 + 4 + i * (len - 8) / 11;
      const mh = i % 5 === 0 ? th * 0.4 : th * 0.25;
      ctx.beginPath();
      ctx.moveTo(mx, -th / 2);
      ctx.lineTo(mx, -th / 2 + mh);
      ctx.stroke();
    }
  } else if (item.type === 'postit') {
    const sz = s * 0.6;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    ctx.fillRect(-sz / 2 + 2, -sz / 2 + 2, sz, sz);
    // Note (slightly tilted)
    ctx.fillStyle = '#ffee55';
    ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
    // Folded corner
    ctx.fillStyle = '#e8d840';
    ctx.beginPath();
    ctx.moveTo(sz / 2, -sz / 2);
    ctx.lineTo(sz / 2, -sz / 2 + sz * 0.2);
    ctx.lineTo(sz / 2 - sz * 0.2, -sz / 2);
    ctx.closePath();
    ctx.fill();
    // "Text" lines
    ctx.strokeStyle = 'rgba(100,80,0,0.2)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const ly = -sz / 2 + 6 + i * 7;
      ctx.beginPath();
      ctx.moveTo(-sz / 2 + 4, ly);
      ctx.lineTo(sz / 2 - 8, ly);
      ctx.stroke();
    }
  }

  ctx.restore();
}

function drawMinimap(ctx: CanvasRenderingContext2D, track: TrackDef, cars: CarState[]) {
  const mmW = 130, mmH = 90;
  const mmX = CANVAS_W - mmW - 10, mmY = 10;
  const pts = track.centerLine;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  const minX = Math.min(...xs) - track.width;
  const maxX = Math.max(...xs) + track.width;
  const minY = Math.min(...ys) - track.width;
  const maxY = Math.max(...ys) + track.width;
  const scale = Math.min((mmW - 10) / (maxX - minX), (mmH - 10) / (maxY - minY));
  const offX = mmX + (mmW - (maxX - minX) * scale) / 2;
  const offY = mmY + (mmH - (maxY - minY) * scale) / 2;

  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(mmX, mmY, mmW, mmH);
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1;
  ctx.strokeRect(mmX, mmY, mmW, mmH);

  // Track path
  ctx.beginPath();
  ctx.moveTo(offX + (pts[0].x - minX) * scale, offY + (pts[0].y - minY) * scale);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(offX + (pts[i].x - minX) * scale, offY + (pts[i].y - minY) * scale);
  }
  ctx.closePath();
  ctx.strokeStyle = '#666';
  ctx.lineWidth = 4;
  ctx.stroke();

  // Boost pad indicators
  for (const bp of track.boostPads) {
    const bx = offX + (bp.x - minX) * scale;
    const by = offY + (bp.y - minY) * scale;
    ctx.fillStyle = '#4488ff';
    ctx.fillRect(bx - 2, by - 2, 4, 4);
  }

  // Car dots
  for (const car of cars) {
    const cx = offX + (car.x - minX) * scale;
    const cy = offY + (car.y - minY) * scale;
    ctx.beginPath();
    ctx.arc(cx, cy, car.isPlayer ? 4 : 3, 0, Math.PI * 2);
    ctx.fillStyle = car.color;
    ctx.fill();
    if (car.isPlayer) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
}

function formatTime(frames: number | undefined | null): string {
  if (frames == null || typeof frames !== 'number' || isNaN(frames) || !isFinite(frames) || frames <= 0) {
    return '0:00.00';
  }
  const totalSeconds = frames / 60;
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  const ms = Math.floor((totalSeconds % 1) * 100);
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function MicroMachinesGame() {
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  // Game state refs (avoid re-renders in game loop)
  const phaseRef = useRef<'menu' | 'racing' | 'results'>('menu');
  const carsRef = useRef<CarState[]>([]);
  const camRef = useRef<Vec2>({ x: 0, y: 0 });
  const raceFrameRef = useRef(0);
  const countdownRef = useRef(0);
  const skidsRef = useRef<SkidMark[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const inputRef = useRef({ up: false, down: false, left: false, right: false });
  const trackIdxRef = useRef(0);
  const mutedRef = useRef(false);
  const startDirRef = useRef<Vec2>({ x: 1, y: 0 });
  const driftSoundCooldownRef = useRef(0);
  const lapDeltaRef = useRef<{ text: string; color: string; timer: number }>({ text: '', color: '', timer: 0 });
  const loopRef = useRef<() => void>(() => {});
  const endRaceRef = useRef<(cars: CarState[], track: TrackDef) => void>(() => {});

  // React UI state
  const [gamePhase, setGamePhase] = useState<'menu' | 'racing' | 'results'>('menu');
  const [selectedTrack, setSelectedTrack] = useState(0);
  const [uiLap, setUiLap] = useState(1);
  const [uiPosition, setUiPosition] = useState(1);
  const [uiRaceTime, setUiRaceTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { isSignedIn, user } = useCurrentUser();
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [playerName, setPlayerName] = useState(() => {
    if (user?.username || user?.fullName || user?.firstName) {
      return user.username || user.fullName || user.firstName;
    }
    if (typeof window !== 'undefined') return localStorage.getItem('mm_player_name') || '';
    return '';
  });
  const [scoreSavedToGlobal, setScoreSavedToGlobal] = useState(false);
  const [finalResults, setFinalResults] = useState<ResultEntry[]>([]);
  const isPortraitMobile = useIsPortraitMobile();

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Leaderboard
  const { data: leaderboardRaw } = useQuery<any>({
    queryKey: ['mm-leaderboard', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try { 
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/games/micromachines/leaderboard', { params, silent: true });
        return res.data;
      }
      catch { return []; }
    },
    staleTime: 30000,
  });

  const leaderboardData: LeaderboardEntry[] = useMemo(() => {
    const rawList: any[] = Array.isArray(leaderboardRaw)
      ? leaderboardRaw
      : Array.isArray(leaderboardRaw?.content)
        ? leaderboardRaw.content
        : Array.isArray(leaderboardRaw?.data)
          ? leaderboardRaw.data
          : [];

    return rawList.map((raw: any) => {
      const totalTime = Number(raw.totalTime ?? raw.metadata?.totalTime ?? raw.score ?? 0);
      const bestLapVal = raw.bestLap ?? raw.metadata?.bestLap;
      const bestLap = bestLapVal != null ? Number(bestLapVal) : undefined;
      const track = raw.track || raw.metadata?.track || 'boardroom';
      return {
        playerName: raw.playerName || 'Anonymous',
        score: typeof raw.score === 'number' ? raw.score : totalTime,
        totalTime: isNaN(totalTime) ? 0 : totalTime,
        bestLap: bestLap !== undefined && !isNaN(bestLap) ? bestLap : undefined,
        track,
        date: raw.date || raw.createdAt,
        metadata: raw.metadata,
      };
    });
  }, [leaderboardRaw]);

  const submitScoreMutation = useMutation({
    mutationFn: async (entry: LeaderboardEntry) => {
      await apiWorker.post('/api/games/micromachines/leaderboard', {
        playerName: entry.playerName,
        score: entry.totalTime,
        game: 'micromachines',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          track: entry.track,
          bestLap: entry.bestLap,
          totalTime: entry.totalTime,
          ...(entry.metadata || {})
        }
      }, { silent: true });
    },
    onSuccess: () => {
      setScoreSavedToGlobal(true);
      toast.success('Race time saved to global leaderboard!');
      queryClient.invalidateQueries({ queryKey: ['mm-leaderboard'] });
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'mm-401' });
      } else {
        toast.error('Could not connect to leaderboard server.');
      }
    },
  });

  const [localLeaderboard, setLocalLeaderboard] = useState<LeaderboardEntry[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const parsed = JSON.parse(localStorage.getItem('mm_leaderboard') || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map((entry: any) => {
        const totalTime = Number(entry.totalTime ?? entry.metadata?.totalTime ?? entry.score ?? 0);
        const bestLapVal = entry.bestLap ?? entry.metadata?.bestLap;
        const bestLap = bestLapVal != null ? Number(bestLapVal) : undefined;
        return {
          playerName: entry.playerName || 'Anonymous',
          score: typeof entry.score === 'number' ? entry.score : totalTime,
          totalTime: isNaN(totalTime) ? 0 : totalTime,
          bestLap: bestLap !== undefined && !isNaN(bestLap) ? bestLap : undefined,
          track: entry.track || entry.metadata?.track || 'boardroom',
          date: entry.date || entry.createdAt,
          metadata: entry.metadata,
        };
      });
    } catch {
      return [];
    }
  });

  const leaderboard = (leaderboardData && leaderboardData.length > 0) ? leaderboardData : localLeaderboard;

  // ---------------------------------------------------------------------------
  // Car factory
  // ---------------------------------------------------------------------------

  const makeCar = useCallback((
    x: number, y: number, angle: number,
    isPlayer: boolean, name: string, color: string,
    skill: number, maxSpd: number
  ): CarState => ({
    x, y, vx: 0, vy: 0, angle, isPlayer, name, color,
    lap: 0, lapStartFrame: 0, lapTimes: [],
    checkpointsPassed: new Set<number>(),
    prevFinishDist: -999, finished: false, finishFrame: 0,
    position: isPlayer ? 1 : 4,
    lastPosition: isPlayer ? 1 : 4,
    posChangeTimer: 0,
    boostTimer: 0,
    oilTimer: 0,
    targetWP: 1, skill, maxSpd, wobble: Math.random() * 100,
  }), []);

  // ---------------------------------------------------------------------------
  // Init Race
  // ---------------------------------------------------------------------------

  const initRace = useCallback((trackIdx: number) => {
    const track = TRACKS[trackIdx];
    trackIdxRef.current = trackIdx;
    const fwd = getStartDir(track);
    startDirRef.current = fwd;
    const right: Vec2 = { x: -fwd.y, y: fwd.x };
    const startAngle = Math.atan2(fwd.y, fwd.x);
    const p0 = track.centerLine[0];

    const gridPos = [
      { row: 0, col: -1 },
      { row: 0, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 1 },
    ];

    const cars: CarState[] = [];
    // Player
    const gp = gridPos[0];
    cars.push(makeCar(
      p0.x - fwd.x * (30 + gp.row * 40) + right.x * gp.col * 20,
      p0.y - fwd.y * (30 + gp.row * 40) + right.y * gp.col * 20,
      startAngle, true, playerName || 'Player', '#FFD600', 1, MAX_SPEED,
    ));
    // AI
    AI_DRIVERS.forEach((ai, i) => {
      const g = gridPos[i + 1];
      cars.push(makeCar(
        p0.x - fwd.x * (30 + g.row * 40) + right.x * g.col * 20,
        p0.y - fwd.y * (30 + g.row * 40) + right.y * g.col * 20,
        startAngle, false, ai.name, ai.color, ai.skill, MAX_SPEED * (0.85 + ai.skill * 0.25),
      ));
    });

    carsRef.current = cars;
    camRef.current = { x: p0.x - CANVAS_W / 2, y: p0.y - CANVAS_H / 2 };
    raceFrameRef.current = 0;
    countdownRef.current = COUNTDOWN_FRAMES;
    skidsRef.current = [];
    particlesRef.current = [];
    inputRef.current = { up: false, down: false, left: false, right: false };
    driftSoundCooldownRef.current = 0;
    lapDeltaRef.current = { text: '', color: '', timer: 0 };

    setUiLap(1);
    setUiPosition(1);
    setUiRaceTime(0);

    phaseRef.current = 'racing';
    setGamePhase('racing');

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => loopRef.current());
  }, [playerName, makeCar]);

  // ---------------------------------------------------------------------------
  // Game Loop
  // ---------------------------------------------------------------------------

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (phaseRef.current === 'menu') return;

    const track = TRACKS[trackIdxRef.current];
    const cars = carsRef.current;
    const cam = camRef.current;
    const inp = inputRef.current;
    const player = cars[0];
    const fwd = startDirRef.current;
    const isCountdown = countdownRef.current > 0;

    // --- Countdown ---
    if (isCountdown) {
      countdownRef.current--;
      if (countdownRef.current === 0) {
        if (!mutedRef.current) playRaceSound('go');
      } else if (countdownRef.current > 0 && countdownRef.current % 60 === 0) {
        if (!mutedRef.current) playRaceSound('countdown');
      }
    }

    // --- Physics (only when countdown finished) ---
    if (!isCountdown && phaseRef.current === 'racing') {
      raceFrameRef.current++;

      // Decay timers
      if (lapDeltaRef.current.timer > 0) lapDeltaRef.current.timer--;

      // Update ALL cars (player + AI)
      for (let ci = 0; ci < cars.length; ci++) {
        const car = cars[ci];
        if (car.finished) continue;

        // Decay car timers
        if (car.boostTimer > 0) car.boostTimer--;
        if (car.oilTimer > 0) car.oilTimer--;
        if (car.posChangeTimer > 0) car.posChangeTimer--;

        const effectiveMaxSpeed = car.boostTimer > 0
          ? (car.isPlayer ? MAX_SPEED * BOOST_SPEED_MULT : car.maxSpd * BOOST_SPEED_MULT)
          : (car.isPlayer ? MAX_SPEED : car.maxSpd);

        const effectiveLateralFriction = car.oilTimer > 0 ? OIL_LATERAL_FRICTION : LATERAL_FRICTION;

        if (car.isPlayer) {
          // --- Player physics ---
          const fx = Math.cos(car.angle), fy = Math.sin(car.angle);
          const fwdSpd = car.vx * fx + car.vy * fy;
          const latSpd = car.vx * (-fy) + car.vy * fx;

          const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
          const turnFactor = Math.min(1, speed / (effectiveMaxSpeed * 0.4));
          const turnMult = car.oilTimer > 0 ? 0.6 : 1;
          if (inp.left) car.angle -= TURN_SPEED * turnFactor * turnMult;
          if (inp.right) car.angle += TURN_SPEED * turnFactor * turnMult;

          let newFwd = fwdSpd;
          if (inp.up) newFwd = Math.min(fwdSpd + ACCEL, effectiveMaxSpeed);
          if (inp.down) newFwd = Math.max(fwdSpd - BRAKE_FORCE, -effectiveMaxSpeed * 0.3);

          const newLat = latSpd * effectiveLateralFriction;
          const nfx = Math.cos(car.angle), nfy = Math.sin(car.angle);
          car.vx = nfx * newFwd + (-nfy) * newLat;
          car.vy = nfy * newFwd + nfx * newLat;

          const onTrack = isOnTrack(car.x, car.y, track);
          const drag = onTrack ? DRAG : OFF_TRACK_DRAG;
          car.vx *= drag;
          car.vy *= drag;
          car.x += car.vx;
          car.y += car.vy;

          // Skid marks when drifting
          if (Math.abs(latSpd) > 1.5 && onTrack) {
            skidsRef.current.push({ x: car.x, y: car.y, alpha: 0.5 });
            if (driftSoundCooldownRef.current <= 0 && !mutedRef.current) {
              playRaceSound('drift');
              driftSoundCooldownRef.current = 10;
            }
          }

          // Drift smoke particles
          if (Math.abs(latSpd) > 2.2 && onTrack) {
            const rearX = car.x - Math.cos(car.angle) * CAR_L * 0.4;
            const rearY = car.y - Math.sin(car.angle) * CAR_L * 0.4;
            particlesRef.current.push({
              x: rearX + (Math.random() - 0.5) * 8,
              y: rearY + (Math.random() - 0.5) * 8,
              vx: (Math.random() - 0.5) * 1.5,
              vy: (Math.random() - 0.5) * 1.5,
              life: 25 + Math.random() * 15,
              maxLife: 40,
              color: '#ddd',
              size: 4 + Math.random() * 4,
            });
          }

          // Exhaust particles when accelerating
          if (inp.up && speed > 1) {
            if (raceFrameRef.current % 3 === 0) {
              const rearX = car.x - Math.cos(car.angle) * CAR_L * 0.5;
              const rearY = car.y - Math.sin(car.angle) * CAR_L * 0.5;
              particlesRef.current.push({
                x: rearX,
                y: rearY,
                vx: -Math.cos(car.angle) * 1.5 + (Math.random() - 0.5) * 0.8,
                vy: -Math.sin(car.angle) * 1.5 + (Math.random() - 0.5) * 0.8,
                life: 15 + Math.random() * 10,
                maxLife: 25,
                color: '#888',
                size: 2 + Math.random() * 2,
              });
            }
          }
        } else {
          // --- AI physics ---
          const target = track.centerLine[car.targetWP];
          const dx = target.x - car.x, dy = target.y - car.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 50 + car.skill * 15) {
            car.targetWP = (car.targetWP + 1) % track.centerLine.length;
          }

          const targetAngle = Math.atan2(dy, dx);
          let angleDiff = targetAngle - car.angle;
          while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
          while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

          const turnRate = TURN_SPEED * car.skill * 0.85 * (car.oilTimer > 0 ? 0.6 : 1);
          if (angleDiff > turnRate) car.angle += turnRate;
          else if (angleDiff < -turnRate) car.angle -= turnRate;
          else car.angle += angleDiff * 0.5;

          car.angle += Math.sin(raceFrameRef.current * 0.02 + car.wobble) * 0.003;

          const aiFx = Math.cos(car.angle), aiFy = Math.sin(car.angle);
          const aiFwdSpd = car.vx * aiFx + car.vy * aiFy;
          const aiLatSpd = car.vx * (-aiFy) + car.vy * aiFx;

          // Rubber-banding: last place gets speed boost, first gets throttled
          let rubberBand = 1.0;
          if (car.position === 4) rubberBand = 1.1;
          else if (car.position === 1) rubberBand = 0.95;

          const targetSpd = effectiveMaxSpeed * rubberBand * (0.88 + Math.sin(raceFrameRef.current * 0.008 + car.wobble) * 0.12);
          let newAiFwd = aiFwdSpd;
          if (aiFwdSpd < targetSpd) newAiFwd = Math.min(aiFwdSpd + ACCEL * car.skill, targetSpd);
          else newAiFwd = Math.max(aiFwdSpd - BRAKE_FORCE * 0.3, targetSpd * 0.9);

          const newAiLat = aiLatSpd * effectiveLateralFriction;
          const naFx = Math.cos(car.angle), naFy = Math.sin(car.angle);
          car.vx = naFx * newAiFwd + (-naFy) * newAiLat;
          car.vy = naFy * newAiFwd + naFx * newAiLat;

          const aiOnTrack = isOnTrack(car.x, car.y, track);
          car.vx *= aiOnTrack ? DRAG : OFF_TRACK_DRAG;
          car.vy *= aiOnTrack ? DRAG : OFF_TRACK_DRAG;
          car.x += car.vx;
          car.y += car.vy;

          // AI skid marks
          if (Math.abs(aiLatSpd) > 2 && aiOnTrack) {
            skidsRef.current.push({ x: car.x, y: car.y, alpha: 0.3 });
          }
          // AI exhaust
          if (raceFrameRef.current % 5 === 0) {
            const rearX = car.x - Math.cos(car.angle) * CAR_L * 0.5;
            const rearY = car.y - Math.sin(car.angle) * CAR_L * 0.5;
            particlesRef.current.push({
              x: rearX, y: rearY,
              vx: -Math.cos(car.angle) * 1 + (Math.random() - 0.5) * 0.5,
              vy: -Math.sin(car.angle) * 1 + (Math.random() - 0.5) * 0.5,
              life: 12, maxLife: 18,
              color: '#777', size: 2,
            });
          }
        }

        // --- Boost pad collision ---
        for (const bp of track.boostPads) {
          const bpDx = car.x - bp.x, bpDy = car.y - bp.y;
          if (bpDx * bpDx + bpDy * bpDy < 30 * 30 && car.boostTimer <= 0) {
            car.boostTimer = BOOST_DURATION;
            if (car.isPlayer && !mutedRef.current) playRaceSound('boost');
            // Boost particles
            for (let p = 0; p < 8; p++) {
              particlesRef.current.push({
                x: car.x, y: car.y,
                vx: (Math.random() - 0.5) * 4,
                vy: (Math.random() - 0.5) * 4,
                life: 20, maxLife: 20,
                color: '#44aaff', size: 3 + Math.random() * 3,
              });
            }
          }
        }

        // --- Oil slick collision ---
        for (const os of track.oilSlicks) {
          const osDx = car.x - os.x, osDy = car.y - os.y;
          if (osDx * osDx + osDy * osDy < os.radius * os.radius && car.oilTimer <= 0) {
            car.oilTimer = OIL_DURATION;
            if (car.isPlayer && !mutedRef.current) playRaceSound('oil');
          }
        }
      }

      if (driftSoundCooldownRef.current > 0) driftSoundCooldownRef.current--;

      // Car-car collisions
      for (let i = 0; i < cars.length; i++) {
        for (let j = i + 1; j < cars.length; j++) {
          const prevDistSq = (cars[i].x - cars[j].x) ** 2 + (cars[i].y - cars[j].y) ** 2;
          if (prevDistSq < (CAR_L * 2) ** 2) {
            resolveCarCollision(cars[i], cars[j]);
          }
        }
      }

      // Checkpoints & lap detection
      for (let ci = 0; ci < cars.length; ci++) {
        const car = cars[ci];
        if (car.finished) continue;
        const nearest = nearestWaypoint(car.x, car.y, track);
        car.checkpointsPassed.add(nearest);

        const dist = getFinishDist(car.x, car.y, track, fwd);
        if (car.prevFinishDist < 0 && dist >= 0) {
          if (car.checkpointsPassed.size >= track.centerLine.length * 0.5) {
            const lapTime = raceFrameRef.current - car.lapStartFrame;
            car.lapTimes.push(lapTime);
            car.lap++;
            car.lapStartFrame = raceFrameRef.current;
            car.checkpointsPassed.clear();

            if (!mutedRef.current) playRaceSound('lap');

            // Lap delta for player
            if (car.isPlayer && car.lapTimes.length >= 2) {
              const prevBest = Math.min(...car.lapTimes.slice(0, -1));
              const delta = lapTime - prevBest;
              const sign = delta >= 0 ? '+' : '';
              lapDeltaRef.current = {
                text: `${sign}${(delta / 60).toFixed(2)}s`,
                color: delta <= 0 ? '#44ff44' : '#ff4444',
                timer: 120,
              };
            }

            for (let p = 0; p < 15; p++) {
              particlesRef.current.push({
                x: car.x, y: car.y,
                vx: (Math.random() - 0.5) * 6,
                vy: (Math.random() - 0.5) * 6,
                life: 40, maxLife: 40,
                color: car.color, size: 3 + Math.random() * 4,
              });
            }

            if (car.lap >= TOTAL_LAPS) {
              car.finished = true;
              car.finishFrame = raceFrameRef.current;
              if (car.isPlayer) {
                if (!mutedRef.current) playRaceSound('finish');
                endRaceRef.current(cars, track);
              }
            }
          }
        }
        car.prevFinishDist = dist;
      }

      // Update positions & detect position changes
      const sorted = cars.map((c, i) => ({ i, prog: getRaceProgress(c, track) }))
        .sort((a, b) => b.prog - a.prog);
      sorted.forEach((entry, rank) => {
        const car = cars[entry.i];
        const newPos = rank + 1;
        if (car.position !== newPos) {
          car.lastPosition = car.position;
          car.position = newPos;
          car.posChangeTimer = 60;
        }
      });

      // Decay skid marks
      const skids = skidsRef.current;
      for (let i = skids.length - 1; i >= 0; i--) {
        skids[i].alpha -= 0.003;
        if (skids[i].alpha <= 0) skids.splice(i, 1);
      }
      if (skids.length > 800) skids.splice(0, skids.length - 800);

      // Update particles
      const parts = particlesRef.current;
      for (let i = parts.length - 1; i >= 0; i--) {
        parts[i].x += parts[i].vx;
        parts[i].y += parts[i].vy;
        parts[i].vx *= 0.97;
        parts[i].vy *= 0.97;
        parts[i].life--;
        if (parts[i].life <= 0) parts.splice(i, 1);
      }
      if (parts.length > 300) parts.splice(0, parts.length - 300);

      // UI sync (throttled)
      if (raceFrameRef.current % 8 === 0) {
        setUiLap(Math.min(player.lap + 1, TOTAL_LAPS));
        setUiPosition(player.position);
        setUiRaceTime(raceFrameRef.current);
      }

      // Auto-end timeout
      if (!player.finished && raceFrameRef.current > 60 * 300) {
        player.finished = true;
        player.finishFrame = raceFrameRef.current;
        endRaceRef.current(cars, track);
      }
    }

    // --- Camera ---
    const targX = player.x - CANVAS_W / 2;
    const targY = player.y - CANVAS_H / 2;
    cam.x += (targX - cam.x) * 0.07;
    cam.y += (targY - cam.y) * 0.07;

    // =================== RENDER ===================

    // Background (desk surface)
    const bgGrad = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
    bgGrad.addColorStop(0, track.bgColor);
    bgGrad.addColorStop(0.5, lightenColor(track.bgColor, 12));
    bgGrad.addColorStop(1, track.bgColor);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Wood grain effect (better: curved overlapping strokes with varying opacity)
    for (let y = -20; y < CANVAS_H + 20; y += 12) {
      const worldY = y + cam.y;
      const wobble = Math.sin(worldY * 0.003) * 18 + Math.sin(worldY * 0.008) * 6;
      const alpha = 0.04 + Math.abs(Math.sin(worldY * 0.006)) * 0.03;
      ctx.strokeStyle = `rgba(0,0,0,${alpha})`;
      ctx.lineWidth = 1 + Math.abs(Math.sin(worldY * 0.01)) * 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y + wobble);
      ctx.quadraticCurveTo(CANVAS_W * 0.5, y + wobble * 0.8 + Math.sin(worldY * 0.007) * 4, CANVAS_W, y + wobble * 0.6);
      ctx.stroke();
    }

    // Subtle desk edge (bottom)
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.fillRect(0, CANVAS_H - 3, CANVAS_W, 3);

    ctx.save();
    ctx.translate(-cam.x, -cam.y);

    // Draw track (with boost pads and oil slicks)
    drawTrack(ctx, track, raceFrameRef.current);

    // Draw scenery (canvas-drawn)
    for (const s of track.scenery) {
      drawSceneryItem(ctx, s);
    }

    // Skid marks (slightly bigger)
    for (const sk of skidsRef.current) {
      ctx.fillStyle = `rgba(25,25,25,${sk.alpha})`;
      ctx.beginPath();
      ctx.arc(sk.x, sk.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Particles (behind cars for exhaust/smoke)
    for (const p of particlesRef.current) {
      const alpha = p.life / p.maxLife;
      ctx.globalAlpha = alpha * 0.7;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Cars (sorted by Y for pseudo-depth)
    const sortedCars = [...cars].sort((a, b) => a.y - b.y);
    for (const car of sortedCars) {
      // Headlight glow in front of car
      const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
      if (speed > 0.5) {
        const glowLen = 20 + speed * 4;
        const glowX = car.x + Math.cos(car.angle) * CAR_L * 0.5;
        const glowY = car.y + Math.sin(car.angle) * CAR_L * 0.5;
        const grad = ctx.createRadialGradient(glowX, glowY, 0, glowX + Math.cos(car.angle) * glowLen * 0.5, glowY + Math.sin(car.angle) * glowLen * 0.5, glowLen);
        grad.addColorStop(0, 'rgba(255,255,200,0.06)');
        grad.addColorStop(1, 'rgba(255,255,200,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(glowX + Math.cos(car.angle) * glowLen * 0.3, glowY + Math.sin(car.angle) * glowLen * 0.3, glowLen, 0, Math.PI * 2);
        ctx.fill();
      }
      drawCar(ctx, car, raceFrameRef.current);
    }

    ctx.restore();

    // =================== HUD ===================

    // Lap counter (top left)
    ctx.font = 'bold 14px "DM Sans", sans-serif';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`LAP ${Math.min(player.lap + 1, TOTAL_LAPS)} / ${TOTAL_LAPS}`, 12, 12);

    // Position
    const pos = player.position;
    const posStr = pos === 1 ? '1ST' : pos === 2 ? '2ND' : pos === 3 ? '3RD' : '4TH';
    ctx.font = 'bold 26px "DM Sans", sans-serif';
    ctx.fillStyle = pos === 1 ? '#FFD600' : pos === 2 ? '#ccc' : pos === 3 ? '#cd7f32' : '#888';
    ctx.fillText(posStr, 12, 30);

    // Position change callout
    if (player.posChangeTimer > 0) {
      const pAlpha = Math.min(1, player.posChangeTimer / 30);
      const pScale = 1 + (1 - pAlpha) * 0.3;
      ctx.save();
      ctx.globalAlpha = pAlpha;
      ctx.font = `bold ${Math.floor(18 * pScale)}px "DM Sans", sans-serif`;
      ctx.fillStyle = player.position < player.lastPosition ? '#44ff44' : '#ff6644';
      ctx.textAlign = 'center';
      ctx.fillText(
        player.position < player.lastPosition ? '▲ ' + posStr : '▼ ' + posStr,
        CANVAS_W / 2, 45
      );
      ctx.restore();
    }

    // Timer (top center)
    ctx.font = '13px "DM Sans", sans-serif';
    ctx.fillStyle = '#ddd';
    ctx.textAlign = 'center';
    ctx.fillText(formatTime(raceFrameRef.current), CANVAS_W / 2, 12);

    // Lap delta
    if (lapDeltaRef.current.timer > 0) {
      const ldAlpha = Math.min(1, lapDeltaRef.current.timer / 40);
      ctx.globalAlpha = ldAlpha;
      ctx.font = 'bold 14px "DM Sans", sans-serif';
      ctx.fillStyle = lapDeltaRef.current.color;
      ctx.textAlign = 'center';
      ctx.fillText(lapDeltaRef.current.text, CANVAS_W / 2, 28);
      ctx.globalAlpha = 1;
    }

    // Speed bar (bottom left)
    const playerSpeed = Math.sqrt(player.vx * player.vx + player.vy * player.vy);
    const speedPct = Math.min(1, playerSpeed / MAX_SPEED);
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(12, CANVAS_H - 24, 90, 10);
    const speedColor = player.boostTimer > 0 ? '#44aaff' : speedPct < 0.5 ? '#0044FF' : speedPct < 0.8 ? '#FFD600' : '#FF4444';
    ctx.fillStyle = speedColor;
    ctx.fillRect(12, CANVAS_H - 24, 90 * speedPct, 10);
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    ctx.strokeRect(12, CANVAS_H - 24, 90, 10);
    ctx.font = '9px "DM Sans", sans-serif';
    ctx.fillStyle = '#888';
    ctx.textAlign = 'left';
    ctx.fillText(player.boostTimer > 0 ? 'BOOST!' : 'SPEED', 12, CANVAS_H - 10);

    // Oil warning
    if (player.oilTimer > 0) {
      ctx.font = 'bold 11px "DM Sans", sans-serif';
      ctx.fillStyle = '#ff8800';
      ctx.textAlign = 'left';
      ctx.fillText('⚠ OIL!', 110, CANVAS_H - 14);
    }

    // Best lap (bottom center)
    if (player.lapTimes.length > 0) {
      const best = Math.min(...player.lapTimes);
      ctx.font = '10px "DM Sans", sans-serif';
      ctx.fillStyle = '#aaa';
      ctx.textAlign = 'center';
      ctx.fillText(`BEST LAP: ${formatTime(best)}`, CANVAS_W / 2, CANVAS_H - 12);
    }

    // Minimap (top right)
    drawMinimap(ctx, track, cars);

    // Countdown overlay
    if (isCountdown) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const secsLeft = Math.ceil(countdownRef.current / 60);
      if (secsLeft <= 3 && secsLeft >= 1) {
        // Traffic light circles
        for (let i = 0; i < 3; i++) {
          ctx.fillStyle = (3 - i) >= secsLeft ? (i === 0 ? '#FF4444' : i === 1 ? '#FFD600' : '#44FF44') : '#333';
          ctx.beginPath();
          ctx.arc(CANVAS_W / 2 - 40 + i * 40, CANVAS_H / 2 - 50, 14, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#555';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        ctx.font = 'bold 70px "DM Sans", sans-serif';
        ctx.fillStyle = secsLeft === 1 ? '#FF4444' : secsLeft === 2 ? '#FFD600' : '#fff';
        ctx.fillText(`${secsLeft}`, CANVAS_W / 2, CANVAS_H / 2 + 20);
      }
    }
    // "GO!" flash
    if (!isCountdown && raceFrameRef.current < 30) {
      const goAlpha = 1 - raceFrameRef.current / 30;
      ctx.globalAlpha = goAlpha;
      ctx.font = 'bold 80px "DM Sans", sans-serif';
      ctx.fillStyle = '#00FF44';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('GO!', CANVAS_W / 2, CANVAS_H / 2);
      ctx.globalAlpha = 1;
    }

    rafRef.current = requestAnimationFrame(() => loopRef.current());
  }, []);

  // ---------------------------------------------------------------------------
  // End Race
  // ---------------------------------------------------------------------------

  const endRace = useCallback((cars: CarState[], track: TrackDef) => {
    phaseRef.current = 'results';
    setGamePhase('results');
    cancelAnimationFrame(rafRef.current);

    const results: ResultEntry[] = cars
      .map(c => ({
        name: c.name,
        color: c.color,
        position: c.position,
        totalFrames: c.finished ? c.finishFrame : raceFrameRef.current,
        bestLap: c.lapTimes.length > 0 ? Math.min(...c.lapTimes) : 999999,
        finished: c.finished,
      }))
      .sort((a, b) => a.position - b.position);

    setFinalResults(results);

    const player = cars[0];
    if (player.lapTimes.length > 0) {
      const entry: LeaderboardEntry = {
        playerName: playerName || (isSignedIn ? (user?.username || user?.fullName || 'Racer') : 'Guest'),
        track: track.id,
        bestLap: Math.min(...player.lapTimes),
        totalTime: player.finished ? player.finishFrame : raceFrameRef.current,
        date: new Date().toISOString(),
      };
      const updated = [...localLeaderboard, entry]
        .sort((a, b) => a.totalTime - b.totalTime)
        .slice(0, 10);
      setLocalLeaderboard(updated);
      localStorage.setItem('mm_leaderboard', JSON.stringify(updated));
      if (isSignedIn) {
        submitScoreMutation.mutate(entry);
      }
    }
  }, [playerName, localLeaderboard, submitScoreMutation, isSignedIn, user]);

  useEffect(() => {
    loopRef.current = loop;
    endRaceRef.current = endRace;
  }, [loop, endRace]);

  // ---------------------------------------------------------------------------
  // Input Handlers
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (phaseRef.current === 'menu') return;
      const inp = inputRef.current;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') { e.preventDefault(); inp.up = true; }
      if (e.code === 'ArrowDown' || e.code === 'KeyS') { e.preventDefault(); inp.down = true; }
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') { e.preventDefault(); inp.left = true; }
      if (e.code === 'ArrowRight' || e.code === 'KeyD') { e.preventDefault(); inp.right = true; }
    };
    const onUp = (e: KeyboardEvent) => {
      const inp = inputRef.current;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') inp.up = false;
      if (e.code === 'ArrowDown' || e.code === 'KeyS') inp.down = false;
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') inp.left = false;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') inp.right = false;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      tryUnlockOrientation();
    };
  }, []);

  // Draw menu canvas
  useEffect(() => {
    if (gamePhase !== 'menu') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    bgGrad.addColorStop(0, '#1a1a2e');
    bgGrad.addColorStop(1, '#0f0f1a');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Title
    ctx.font = 'bold 42px "DM Sans", sans-serif';
    ctx.fillStyle = '#FFD600';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('MICRO MACHINES', CANVAS_W / 2, 55);

    ctx.font = 'bold 18px "DM Sans", sans-serif';
    ctx.fillStyle = '#0044FF';
    ctx.fillText('OFFICE EDITION', CANVAS_W / 2, 85);

    // Subtitle
    ctx.font = '13px "DM Sans", sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText('Top-Down Racing • 3 Tracks • 3 Laps • Beat the AI', CANVAS_W / 2, 110);

    // Draw preview cars (proper cars instead of rectangles)
    const previewColors = ['#FFD600', '#FF4444', '#44DD44', '#DD44FF'];
    const positions = [
      { x: CANVAS_W / 2 - 50, y: 160, angle: 0.3 },
      { x: CANVAS_W / 2 + 30, y: 155, angle: 0.15 },
      { x: CANVAS_W / 2 - 20, y: 175, angle: -0.1 },
      { x: CANVAS_W / 2 + 60, y: 178, angle: 0.4 },
    ];
    positions.forEach((pos, i) => {
      const mockCar = {
        x: pos.x, y: pos.y, vx: 0, vy: 0, angle: pos.angle,
        isPlayer: i === 0, name: '', color: previewColors[i],
        lap: 0, lapStartFrame: 0, lapTimes: [],
        checkpointsPassed: new Set<number>(),
        prevFinishDist: 0, finished: false, finishFrame: 0,
        position: i + 1, lastPosition: i + 1, posChangeTimer: 0,
        boostTimer: i === 0 ? 1 : 0, oilTimer: 0,
        targetWP: 0, skill: 0, maxSpd: 0, wobble: 0,
      } as CarState;
      drawCar(ctx, mockCar, 0);
    });

    // Feature pills
    ctx.font = '10px "DM Sans", sans-serif';
    const features = ['🏎️ BOOST PADS', '🛢️ OIL SLICKS', '💨 DRIFT SMOKE', '🏆 LEADERBOARD'];
    features.forEach((f, i) => {
      const fx = CANVAS_W / 2 - 160 + i * 90;
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fillRect(fx, 200, 80, 18);
      ctx.fillStyle = '#888';
      ctx.textAlign = 'center';
      ctx.fillText(f, fx + 40, 212);
    });

    // Controls
    ctx.font = '11px "DM Sans", sans-serif';
    ctx.fillStyle = '#555';
    ctx.fillText('⬆️ Accelerate  ⬇️ Brake  ⬅️➡️ Steer  •  WASD also works', CANVAS_W / 2, 240);

    // "Select track below" hint
    ctx.font = 'bold 14px "DM Sans", sans-serif';
    ctx.fillStyle = '#888';
    ctx.fillText('▼ SELECT A TRACK BELOW TO RACE ▼', CANVAS_W / 2, 270);
  }, [gamePhase]);

  // Fullscreen
  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      await el.requestFullscreen?.().catch(() => {});
      if (isMobileDevice()) await tryLockLandscape();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen?.().catch(() => {});
      tryUnlockOrientation();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  // Mobile touch handlers
  const touchStartHandler = useCallback((dir: 'up' | 'down' | 'left' | 'right') => {
    inputRef.current[dir] = true;
  }, []);
  const touchEndHandler = useCallback((dir: 'up' | 'down' | 'left' | 'right') => {
    inputRef.current[dir] = false;
  }, []);

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="w-full max-w-4xl mx-auto" ref={containerRef}>
      {/* Portrait mobile warning */}
      {isPortraitMobile && (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center text-white gap-4 p-8">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
            <path d="M12 18h.01" />
            <path d="M2 12 C2 6 6 2 12 2" />
            <polyline points="8 2 12 2 12 6" />
          </svg>
          <p className="text-lg font-bold text-center">Rotate to landscape</p>
          <p className="text-sm text-gray-400 text-center">Micro Machines plays best in landscape</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-black text-white tracking-tight">MICRO MACHINES</h2>
          <span className="text-xs text-gray-500 font-mono tracking-wider">OFFICE EDITION</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowLeaderboard(true)} className="p-2 rounded hover:bg-white/10 text-yellow-400 transition-colors" title="Leaderboard">
            <Trophy size={18} />
          </button>
          <button onClick={() => setMuted(!muted)} className="p-2 rounded hover:bg-white/10 text-gray-400 transition-colors" title={muted ? 'Unmute' : 'Mute'}>
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button onClick={toggleFullscreen} className="p-2 rounded hover:bg-white/10 text-gray-400 transition-colors" title="Fullscreen">
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative border-2 border-white/10 rounded-lg overflow-hidden bg-black">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="w-full block"
        />

        {/* Results overlay */}
        <AnimatePresence>
          {gamePhase === 'results' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0.8, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="text-center w-full max-w-sm px-4"
              >
                <h3 className="text-2xl font-black text-yellow-400 mb-1 tracking-tight">
                  {finalResults[0]?.name === (playerName || 'Player') ? '🏆 YOU WIN!' : 'RACE COMPLETE'}
                </h3>
                <p className="text-xs text-gray-400 mb-4">
                  {TRACKS[selectedTrack].name} — {TOTAL_LAPS} Laps
                </p>

                <div className="space-y-2 mb-4">
                  {finalResults.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between py-2 px-3 rounded text-sm ${
                        r.name === (playerName || 'Player') ? 'bg-yellow-400/15 border border-yellow-400/30' : 'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`font-black text-lg ${
                          i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gray-500'
                        }`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '4th'}
                        </span>
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: r.color }} />
                        <span className="text-white font-bold">{r.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-mono text-xs">
                          {r.finished ? formatTime(r.totalFrames) : 'DNF'}
                        </div>
                        {r.bestLap < 999999 && (
                          <div className="text-gray-500 text-[10px]">
                            Best: {formatTime(r.bestLap)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Name input / Sign in CTA */}
                {isSignedIn ? (
                  <div className="mb-4">
                    <input
                      type="text"
                      value={playerName}
                      onChange={(e) => {
                        const name = e.target.value.slice(0, 12);
                        setPlayerName(name);
                        localStorage.setItem('mm_player_name', name);
                      }}
                      placeholder="Your name..."
                      className="bg-white/10 border border-white/20 text-white text-center px-4 py-2 rounded text-sm w-44 placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                    />
                    <div className="text-[11px] text-emerald-400 font-mono mt-1">
                      {scoreSavedToGlobal ? '✓ Score recorded on global leaderboard' : submitScoreMutation.isPending ? 'Saving score to global leaderboard...' : '✓ Race completed'}
                    </div>
                  </div>
                ) : (
                  <div className="bg-black/80 border border-yellow-400/50 p-3 rounded max-w-xs mx-auto mb-4 text-center">
                    <p className="text-[11px] text-gray-300 font-mono mb-1">Score saved to local device.</p>
                    <p className="text-xs font-bold text-white mb-2">Sign in to save your score to the global leaderboard.</p>
                    <Link
                      to="/sign-in"
                      className="inline-block w-full py-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold uppercase text-xs tracking-wider transition-colors rounded"
                    >
                      Sign In to Save Score
                    </Link>
                  </div>
                )}

                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => initRace(trackIdxRef.current)}
                    className="flex items-center gap-2 bg-[#0044FF] hover:bg-blue-700 text-white font-bold py-3 px-5 transition-colors uppercase tracking-wider text-xs"
                  >
                    <RotateCcw size={14} /> Retry
                  </button>
                  <button
                    onClick={() => { phaseRef.current = 'menu'; setGamePhase('menu'); }}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-5 transition-colors uppercase tracking-wider text-xs"
                  >
                    Tracks
                  </button>
                  <button
                    onClick={() => setShowLeaderboard(true)}
                    className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-5 transition-colors uppercase tracking-wider text-xs"
                  >
                    <Trophy size={14} /> Scores
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Live HUD below canvas */}
      {gamePhase === 'racing' && (
        <div className="flex items-center justify-between mt-2 px-2 text-sm">
          <span className={`font-black font-mono ${
            uiPosition === 1 ? 'text-yellow-400' : uiPosition === 2 ? 'text-gray-300' : 'text-gray-500'
          }`}>
            {uiPosition === 1 ? '1ST' : uiPosition === 2 ? '2ND' : uiPosition === 3 ? '3RD' : '4TH'}
          </span>
          <span className="text-gray-400 font-mono">Lap {uiLap}/{TOTAL_LAPS}</span>
          <span className="text-gray-400 font-mono text-xs">{formatTime(uiRaceTime)}</span>
        </div>
      )}

      {/* Track selection (menu phase) */}
      {gamePhase === 'menu' && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {TRACKS.map((t, i) => (
              <button
                key={t.id}
                onClick={() => { setSelectedTrack(i); initRace(i); }}
                className={`text-left border rounded-lg p-4 transition-all hover:scale-[1.02] ${
                  selectedTrack === i
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-white/10 bg-white/5 hover:border-white/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">{t.emoji}</span>
                  <div>
                    <div className="text-white font-black text-sm">{t.name}</div>
                    <div className="text-gray-500 text-[10px]">{t.tagline}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Width: {t.width}px • {t.centerLine.length} turns • {t.boostPads.length} boosts • {t.oilSlicks.length} oil
                </div>
              </button>
            ))}
          </div>

          {/* Controls hint */}
          <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
            <div className="border border-white/10 rounded p-3 bg-white/5">
              <div className="font-bold text-white mb-1">🎮 Controls</div>
              <div>⬆️ / W = Accelerate</div>
              <div>⬇️ / S = Brake / Reverse</div>
              <div>⬅️ / A = Steer Left</div>
              <div>➡️ / D = Steer Right</div>
            </div>
            <div className="border border-white/10 rounded p-3 bg-white/5">
              <div className="font-bold text-white mb-1">🏁 Race Info</div>
              <div>3 laps per race • 3 AI opponents</div>
              <div>🔵 Blue arrows = speed boost pads</div>
              <div>⚫ Dark puddles = oil slicks (watch out!)</div>
              <div>Drift for tire marks! 🏎️💨</div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile touch controls */}
      {isMobileDevice() && gamePhase === 'racing' && (
        <div className="flex gap-2 mt-3 justify-center">
          <button
            onTouchStart={(e) => { e.preventDefault(); touchStartHandler('left'); }}
            onTouchEnd={() => touchEndHandler('left')}
            className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-4 px-6 rounded-lg text-lg select-none touch-none"
          >
            ⬅️
          </button>
          <div className="flex flex-col gap-1">
            <button
              onTouchStart={(e) => { e.preventDefault(); touchStartHandler('up'); }}
              onTouchEnd={() => touchEndHandler('up')}
              className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-3 px-6 rounded-lg text-lg select-none touch-none"
            >
              ⬆️
            </button>
            <button
              onTouchStart={(e) => { e.preventDefault(); touchStartHandler('down'); }}
              onTouchEnd={() => touchEndHandler('down')}
              className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-3 px-6 rounded-lg text-lg select-none touch-none"
            >
              ⬇️
            </button>
          </div>
          <button
            onTouchStart={(e) => { e.preventDefault(); touchStartHandler('right'); }}
            onTouchEnd={() => touchEndHandler('right')}
            className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-4 px-6 rounded-lg text-lg select-none touch-none"
          >
            ➡️
          </button>
        </div>
      )}

      {/* Leaderboard Modal */}
      <AnimatePresence>
        {showLeaderboard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={() => setShowLeaderboard(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#111] border-2 border-yellow-400/30 rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-black text-yellow-400 flex items-center gap-2">
                  <Trophy size={20} /> BEST TIMES
                </h3>
                <button onClick={() => setShowLeaderboard(false)} className="p-1 hover:bg-white/10 rounded text-gray-400">✕</button>
              </div>

              {hasActiveNode && (
                <GameLeaderboardScopeTabs
                  scope={leaderboardScope}
                  onScopeChange={setLeaderboardScope}
                  nodeName={nodeName}
                />
              )}
              {leaderboard.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No times recorded yet. Race to set a record!</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {leaderboard.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between py-2 px-3 rounded ${
                        i === 0 ? 'bg-yellow-400/10 border border-yellow-400/30' :
                        i === 1 ? 'bg-gray-300/5 border border-gray-400/20' :
                        i === 2 ? 'bg-orange-400/5 border border-orange-400/20' : 'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-black w-6 text-center ${
                          i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gray-500'
                        }`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                        </span>
                        <span className="text-white font-bold text-sm">{entry.playerName}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-yellow-400 font-mono text-sm">{formatTime(entry.totalTime || entry.score)}</div>
                        <div className="text-gray-500 text-[10px]">
                          {TRACKS.find(t => t.id === (entry.track || entry.metadata?.track))?.name || entry.track || entry.metadata?.track || 'The Boardroom'}
                          {(entry.bestLap ?? entry.metadata?.bestLap) != null && (entry.bestLap ?? entry.metadata?.bestLap) < 999999 ? ` • Best: ${formatTime(entry.bestLap ?? entry.metadata?.bestLap)}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function lightenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0x00FF) + amount);
  const b = Math.min(255, (num & 0x0000FF) + amount);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

function darkenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
  const b = Math.max(0, (num & 0x0000FF) - amount);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}
